/**
 * Two ways to use the same product.
 *
 * MANUAL is the dashboard: pages, tables, forms, with the agent available in a
 * rail beside them. It is the mode for doing a known thing — issue this
 * invoice, check that customer.
 *
 * AGENT turns the whole surface into a conversation: the workspace's threads on
 * the left, one thread filling the rest, with the dashboard reachable but not
 * in the way. It is the mode for asking rather than navigating — "what changed
 * this week", "why is cash down" — where knowing which of forty screens holds
 * the answer is the hard part.
 *
 * The MODE IS A PREFERENCE, not a route. Somebody who works in agent mode
 * should find it that way tomorrow, and switching modes must not lose the page
 * they were on — which is why this is context plus localStorage rather than
 * `/app/chat` and `/app/dashboard` being different worlds.
 *
 * Stored per user, not per workspace, unlike the agent's thread pointer: how
 * you like to work follows you between your companies, whereas which
 * conversation you were in does not.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type AppMode = 'manual' | 'agent';

const MODE_KEY = 'hipilot_app_mode';

type Ctx = {
  mode: AppMode;
  setMode: (m: AppMode) => void;
  toggle: () => void;
  isAgent: boolean;
  /**
   * Leave the full view WITHOUT losing the conversation.
   *
   * Navigating to a page from agent mode used to switch to manual and leave
   * the chat nowhere — the conversation simply vanished, which reads as having
   * lost it rather than having put it away. This moves it to the rail instead,
   * so the same thread is still there beside the page you asked for.
   *
   * The rail's open state lives in the shell, so this raises a flag the shell
   * consumes on the next render rather than reaching across to set it.
   */
  dockChat: () => void;
  /** True for one render after dockChat(); the shell opens the rail and clears it. */
  wantsDock: boolean;
  clearDock: () => void;
};

const AgentModeContext = createContext<Ctx | null>(null);

function read(): AppMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'agent' ? 'agent' : 'manual';
  } catch {
    return 'manual';
  }
}

export function AgentModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<AppMode>(read);

  const [wantsDock, setWantsDock] = useState(false);
  const dockChat = useCallback(() => {
    setWantsDock(true);
    setModeState('manual');
    try {
      localStorage.setItem(MODE_KEY, 'manual');
    } catch {
      /* private browsing */
    }
  }, []);
  const clearDock = useCallback(() => setWantsDock(false), []);

  const setMode = useCallback((m: AppMode) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* private browsing — the mode still applies for this session */
    }
  }, []);

  /*
    The mode is on the document as well as in React, so CSS can react to it
    without every component threading a prop — the sidebar narrows in agent
    mode, for instance.
  */
  useEffect(() => {
    document.documentElement.dataset.appMode = mode;
  }, [mode]);

  /*
    `wantsDock` HAS TO BE IN THE DEPENDENCIES.

    With only [mode, setMode] the flag is invisible to consumers after the first
    time: clearDock() flips the state but the memo does not recompute, so the
    context still says wantsDock is true, and the second dockChat() changes
    nothing anyone can observe — the shell's effect never re-runs and the rail
    never opens. The first dock worked only because `mode` happened to change in
    the same commit.
  */
  const value = useMemo<Ctx>(
    () => ({
      mode,
      setMode,
      dockChat,
      wantsDock,
      clearDock,
      toggle: () => setMode(mode === 'agent' ? 'manual' : 'agent'),
      isAgent: mode === 'agent',
    }),
    [mode, setMode, dockChat, wantsDock, clearDock],
  );

  return <AgentModeContext.Provider value={value}>{children}</AgentModeContext.Provider>;
}

export function useAppMode(): Ctx {
  const ctx = useContext(AgentModeContext);
  if (!ctx) throw new Error('useAppMode must be used inside <AgentModeProvider>');
  return ctx;
}
